import WebApp from '@twa-dev/sdk';
import { logger } from './logger';

/**
 * Check if we're in development mode
 */
const isDevMode = import.meta.env.VITE_TG_DEV_MODE === 'true';

/**
 * Mock initData for development (when not in real Telegram environment)
 */
const mockInitData = import.meta.env.VITE_TG_INIT_DATA_MOCK || '';

/**
 * Track the current MainButton handler so we can remove it before adding a new one.
 */
let _currentMainButtonHandler: (() => void) | null = null;

/**
 * Telegram WebApp utility functions
 */
export const telegram = {
  /**
   * Get Telegram user info
   */
  getUser: () => {
    return WebApp.initDataUnsafe.user;
  },

  /**
   * Get bot username STRICTLY from Telegram launch signals (NO cache fallback).
   *
   * Used by authStore.bootstrap to detect bot context switching:
   * - Returns the slug the user is currently launching with (via tenant bot)
   * - If null, FE doesn't know which bot from launch — bootstrap relies on cache
   *
   * Priority:
   * 1. Telegram start_param (deep link)
   * 2. URL query parameter (?bot= or ?slug=)
   *
   * Returns null if neither signal is present (page refresh, direct URL, etc).
   */
  getInitialBotUsername: (): string | null => {
    const startParam = WebApp.initDataUnsafe.start_param;
    if (startParam) {
      logger.debug('Initial bot slug from start_param', { botUsername: startParam });
      return startParam;
    }

    const params = new URLSearchParams(window.location.search);
    const botParam = params.get('bot') || params.get('slug');
    if (botParam) {
      logger.debug('Initial bot slug from URL param', { botUsername: botParam });
      return botParam;
    }

    return null;
  },

  /**
   * Get bot username from Telegram WebApp context.
   *
   * Priority:
   * 1. From Telegram start_param (passed via bot deep link)
   * 2. From URL query parameter (?bot= or ?slug=)
   * 3. From cached tenantSlug (saved after previous successful session)
   * 4. From environment variable (development fallback)
   *
   * Note: Telegram initData does NOT expose which bot opened the WebApp,
   * so we rely on these signals in order. The cached slug (priority 3)
   * ensures re-auth works correctly after token expiry.
   */
  getBotUsername: () => {
    // 1. Telegram start_param (deep link: /start <tenantSlug>)
    const startParam = WebApp.initDataUnsafe.start_param;
    if (startParam) {
      logger.debug('Bot username from start_param', { botUsername: startParam });
      return startParam;
    }

    // 2. URL query parameter (?bot= or ?slug=)
    const params = new URLSearchParams(window.location.search);
    const botParam = params.get('bot') || params.get('slug');
    if (botParam) {
      logger.debug('Bot username from URL param', { botUsername: botParam });
      return botParam;
    }

    // 3. Cached from previous successful session (survives token expiry)
    const cachedSlug = localStorage.getItem('tenantSlug');
    if (cachedSlug) {
      logger.debug('Bot username from cached tenantSlug', { botUsername: cachedSlug });
      return cachedSlug;
    }

    // 4. Environment variable — DEV only. In production this fallback is intentionally
    //    absent: unknown tenant must show an error, not silently use a default slug.
    if (import.meta.env.DEV) {
      const envBot = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;
      if (envBot) {
        logger.warn('DEV: Bot username from env variable', { botUsername: envBot });
        return envBot;
      }
    }

    logger.warn('Bot username not found — user must open via bot button');
    return '';
  },

  /**
   * Get Telegram initData (for backend validation)
   *
   * Priority:
   * 1. Real Telegram initData (if available) - ALWAYS preferred
   * 2. Mock initData (for local development without Telegram)
   */
  getInitData: () => {
    // Priority 1: Real Telegram initData (always preferred)
    if (WebApp.initData && WebApp.initData.trim() !== '') {
      logger.info('✅ Using REAL Telegram initData', {
        length: WebApp.initData.length,
        hasUser: WebApp.initData.includes('user='),
        hasAuthDate: WebApp.initData.includes('auth_date='),
        hasHash: WebApp.initData.includes('hash='),
      });
      return WebApp.initData;
    }

    // Priority 2: Mock initData (for local development)
    if (isDevMode && mockInitData) {
      logger.warn('⚠️ DEV MODE: Using MOCK initData (no real Telegram data available)', {
        length: mockInitData.length,
      });
      return mockInitData;
    }

    // No initData available
    logger.error('❌ No Telegram initData available!', {
      isDevMode,
      hasMockData: !!mockInitData,
      platform: WebApp.platform,
    });
    return '';
  },

  /**
   * Show Telegram alert
   */
  showAlert: (message: string) => {
    WebApp.showAlert(message);
  },

  /**
   * Show Telegram confirmation dialog
   */
  showConfirm: (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      WebApp.showConfirm(message, resolve);
    });
  },

  /**
   * Close WebApp
   */
  close: () => {
    WebApp.close();
  },

  /**
   * Set and show main button.
   * Removes the previous handler before adding the new one to prevent accumulation.
   */
  setMainButton: (text: string, onClick: () => void) => {
    if (_currentMainButtonHandler) {
      WebApp.MainButton.offClick(_currentMainButtonHandler);
    }
    _currentMainButtonHandler = onClick;
    WebApp.MainButton.setText(text);
    WebApp.MainButton.onClick(onClick);
    WebApp.MainButton.show();
  },

  /**
   * Hide main button and remove the current handler.
   */
  hideMainButton: () => {
    if (_currentMainButtonHandler) {
      WebApp.MainButton.offClick(_currentMainButtonHandler);
      _currentMainButtonHandler = null;
    }
    WebApp.MainButton.hide();
  },

  /**
   * Show loading indicator on main button
   */
  showMainButtonLoader: () => {
    WebApp.MainButton.showProgress();
  },

  /**
   * Hide loading indicator on main button
   */
  hideMainButtonLoader: () => {
    WebApp.MainButton.hideProgress();
  },

  /**
   * Open a Telegram chat/bot by username inside the Telegram client.
   * e.g. openTelegramUser('BotFather') → opens https://t.me/BotFather.
   */
  openTelegramUser: (username: string) => {
    const handle = username.replace(/^@/, '');
    WebApp.openTelegramLink(`https://t.me/${handle}`);
  },

  /**
   * Read plain text from the clipboard.
   *
   * Tries Telegram's native readTextFromClipboard first (Bot API 6.4+),
   * then falls back to the browser Clipboard API. Resolves null when
   * nothing readable is available — callers MUST handle the empty case
   * (e.g. apps not launched from the attachment menu get an empty result).
   *
   * No clipboard content is logged — it may contain a sensitive bot token.
   */
  readClipboardText: (): Promise<string | null> => {
    return new Promise((resolve) => {
      let settled = false;
      const done = (value: string | null) => {
        if (settled) return;
        settled = true;
        resolve(value && value.trim() ? value.trim() : null);
      };

      const tryBrowserClipboard = () => {
        if (navigator.clipboard?.readText) {
          navigator.clipboard
            .readText()
            .then((text) => done(text))
            .catch(() => done(null));
        } else {
          done(null);
        }
      };

      // Safety net: never leave the promise pending if no path resolves.
      setTimeout(() => done(null), 3000);

      try {
        if (typeof WebApp.readTextFromClipboard === 'function') {
          WebApp.readTextFromClipboard((text) => {
            if (text && text.trim()) {
              done(text);
            } else {
              // Telegram returned empty → fall back to the browser clipboard.
              tryBrowserClipboard();
            }
          });
          return;
        }
      } catch {
        // fall through to browser clipboard
      }
      tryBrowserClipboard();
    });
  },

  /**
   * Open Telegram chat with a client.
   * Priority: telegramId → phone → toast fallback
   */
  openClientChat: ({ telegramId, phone }: { telegramId?: number; phone?: string }) => {
    if (telegramId) {
      WebApp.openTelegramLink(`tg://user?id=${telegramId}`);
      return;
    }
    if (phone) {
      const normalized = phone.replace(/\D/g, '');
      WebApp.openLink(`https://t.me/+${normalized}`);
      return;
    }
    WebApp.showAlert("Mijoz aloqa ma'lumotlari mavjud emas");
  },

  /**
   * Haptic feedback
   */
  hapticFeedback: {
    impact: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => {
      WebApp.HapticFeedback.impactOccurred(style);
    },
    notification: (type: 'error' | 'success' | 'warning') => {
      WebApp.HapticFeedback.notificationOccurred(type);
    },
    selection: () => {
      WebApp.HapticFeedback.selectionChanged();
    },
  },
};
